import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import type { CopyDashboardResult } from "../../../../../api/types/dashboardsTypes";
import type { SelectedCompany } from "../../hooks/validationStateSlice";
import type { CopyCompany, CopyCompanyDashboard, CopyCompanyTile } from "./CopyCompany";

export type CopyingState = {
  companies: CopyCompany[];
};

const initialState: CopyingState = { companies: [] };

const copyingStateSlice = createSlice({
  name: "copyingState",
  initialState: initialState,
  reducers: {
    setCompaniesAction: (state, action: PayloadAction<{ companies: SelectedCompany[] }>) => {
      const { companies } = action.payload;

      state.companies = companies.map((c): CopyCompany => ({
        company: c,
        dashboards: c.dashboards.map((r): CopyCompanyDashboard => ({
          toCompanyCode: c.code,
          copying: false,
          copied: false,
          dashboard: r.dashboard,
          tiles: r.tiles.map((t): CopyCompanyTile => ({
            toCompanyCode: c.code,
            dashboard: r.dashboard,
            tile: t,
            copying: false,
            copied: false,
          })),
        })),
      }));
    },
    updateDashboardAction: (
      state,
      action: PayloadAction<{ dashboard: CopyCompanyDashboard; changes: Partial<CopyCompanyDashboard> }>
    ) => {
      const dashboard = state.companies
        .find((c) => c.company.code === action.payload.dashboard.toCompanyCode)
        ?.dashboards.find((r) => r.dashboard.dashboardId === action.payload.dashboard.dashboard.dashboardId);

      if (dashboard) {
        Object.assign(dashboard, action.payload.changes);
      }
    },
    updateCopyResultAction: (
      state,
      action: PayloadAction<{ dashboard: CopyCompanyDashboard; result: CopyDashboardResult }>
    ) => {
      const dashboard = state.companies
        .find((c) => c.company.code === action.payload.dashboard.toCompanyCode)
        ?.dashboards.find((r) => r.dashboard.dashboardId === action.payload.dashboard.dashboard.dashboardId);

      if (dashboard) {
        dashboard.copying = false;
        dashboard.copied = action.payload.result.success;
        dashboard.error = action.payload.result.errorMessage;
        dashboard.newDashboardId = action.payload.result.newDashboardId;
        action.payload.result.tileCopyResults.forEach((r) => {
          const tile = dashboard.tiles.find((t) => t.tile.tile.tileId === r.tileId);
          if (tile) {
            tile.copied = r.success;
            tile.error = r.errorMessage;
          }
        });
      }
    },
  },
});

export default copyingStateSlice;

export const actions = copyingStateSlice.actions;
